import { useState, useEffect, useRef } from 'react';
import WizardUpdateConfirmModal from './WizardUpdateConfirmModal';
import SplitUpdateConfirmModal from './SplitUpdateConfirmModal';
import BudgetSyncModal from './BudgetSyncModal';
import DebouncedNumberInput from './DebouncedNumberInput';
import ConfirmModal from './ConfirmModal';
import TagRuleDrawer from './TagRuleDrawer';
import TransferFields from './IntentFields/TransferFields';
import DefaultFields from './IntentFields/DefaultFields';
import PersonalFields from './IntentFields/PersonalFields';
import { useOrgConfig } from './Hooks/OrgConfigHooks';
import TaxFields from './IntentFields/TaxFields';
import { APIClient } from "../helpers/api_helper";
import { CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setFilterData } from '../slices/filters/thunk';
import { loadTagRules } from '../slices/thunk';
import { matchTagRule } from './tagRuleMatch';
import {
  buildSavePayload, checkBudgetSync, confirmStepFor, intentWithDayjsDates, num, round2,
  splitModeAmounts, splitsOfIntent, submitIntentSave,
} from './intentSave';
import usecustomStyles from "./customStyles";
import IntentTypeSelect from "./IntentTypeSelect";
import TaxRateSelect from "./TaxRateSelect";
import { Alert, Button, Checkbox, Col, Divider, Drawer, Form,
  Input, Row, Select, Segmented, Space, Switch, Tooltip,
} from 'antd';
import { useWatch } from 'antd/es/form/Form';
import MoneyDisplay from '../Common/MoneyDisplay';
import HistoryTimeline from './HistoryTimeline';
import IntentComments from './IntentComments';

const { Option } = Select;
const { TextArea } = Input;
const customStyles = usecustomStyles();
const api = new APIClient();

// Boolean columns without null=True. In mass edit their BoolToggle is tri-state, where
// null stands for "leave unchanged" and must never be sent to bulk_update.
const NON_NULLABLE_BOOL_FIELDS = ['is_paid', 'is_issued'];

const OverrideLabel = ({ label, field, overriddenFields }) => (
  <span>
    {label}
    {overriddenFields?.includes(field) && (
      <Tooltip title="Vrednost je bila ročno spremenjena.">
        <EditOutlined style={{ color: '#1677ff', marginLeft: 4 }} />
      </Tooltip>
    )}
  </span>
);

const IntentDrawer = (props) => {
  const orgConfig = useOrgConfig();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const repeatPeriod = useWatch('repeat_period', form);
  const amount = useWatch('amount', form);

  const [activeDrawerTab, setActiveDrawerTab] = useState('data');
  const [showWizardUpdateConfirmModal, setshowWizardUpdateConfirmModal] = useState(false);
  const [showBudgetSyncModal, setShowBudgetSyncModal] = useState(false);
  const [showDeletionConfirmModal, setShowDeletionConfirmModal] = useState(false);
  const [showMassEditConfirmModal, setShowMassEditConfirmModal] = useState(false);
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [showSplitConfirmModal, setShowSplitConfirmModal] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [splitEditMode, setSplitEditMode] = useState('master');
  const [wizardEditMode, setWizardEditMode] = useState('all');
  const [includePastUnpaid, setIncludePastUnpaid] = useState(false);
  const [includePastPaid, setIncludePastPaid] = useState(false);
  const [activeEditDimension, setActiveEditDimension] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [pendingMassEditValues, setPendingMassEditValues] = useState(null);
  const [pendingValues, setPendingValues] = useState(null);
  const [intentData, setIntentData] = useState(null);
  const [saving, setSaving] = useState(false);
  const typeValue = useWatch('type', form);
  // Bumped whenever the drawer (re)initialises, to remount TaxRateSelect so its
  // "custom rate box is open" latch doesn't survive into the next intent.
  const [formEpoch, setFormEpoch] = useState(0);

  const isMassEdit = props.massEdit === true;
  const isEdit = props.editedIntentId !== null && !isMassEdit;

  // --- tag rules: prefill the tag from the title as the user types ---------
  // Loaded once at app start (Authprotected), already filtered to is_active and
  // in priority order.
  const tagRules = useSelector((state) => state.TagRules.list);

  // Ref, not state, deliberately. onValuesChange is a plain callback and the
  // prefill runs in the SAME tick as the keystroke that triggered it; under
  // React 18 batching a queued setState has not committed by the time the next
  // keystroke reads the flag, so a suggestion the user just cleared would come
  // straight back. A ref is read-your-writes -- the same reason
  // DebouncedNumberInput keeps its editing flag in one.
  const tagTouchedRef = useRef(false);
  // State: this only drives the hint's rendering, no same-tick decision reads it.
  const [tagRuleHint, setTagRuleHint] = useState(null); // {ruleId, tagId, pattern}
  // {pattern, tag} while the "Vedno označi tako" drawer is open.
  const [tagRuleDrawerDefaults, setTagRuleDrawerDefaults] = useState(null);

  const resetTagRuleSession = () => {
    tagTouchedRef.current = false;
    setTagRuleHint(null);
  };

  const maybePrefillTag = (title) => {
    // Mass edit applies one title to N transactions; prefilling a tag there
    // would silently stamp it onto every selected row.
    if (isMassEdit) return;
    // Don't race the edit form's own load: intentData and its setFieldsValue
    // land asynchronously, and matching before they do could overwrite a real tag.
    if (isEdit && intentData == null) return;
    // The user has picked or cleared the tag themselves -- hands off.
    if (tagTouchedRef.current) return;

    const currentTag = form.getFieldValue('tag');
    const isOurs = tagRuleHint != null && currentTag === tagRuleHint.tagId;
    // A tag we did not put there (loaded from the server, or from
    // defaultValues) is off limits. Only an empty field, or our own suggestion,
    // may be rewritten. Without the isOurs half we would bail on every
    // keystroke after the first -- because WE just made the field non-empty --
    // and backspacing past the match would strand a stale tag.
    if (currentTag && !isOurs) return;

    const rule = matchTagRule(title, tagRules);

    if (!rule) {
      // Typed past the match: take our own suggestion back, and only ours.
      if (isOurs) {
        form.setFieldsValue({ tag: null });
        setTagRuleHint(null);
      }
      return;
    }
    if (isOurs && rule.id === tagRuleHint.ruleId) return; // nothing changed

    form.setFieldsValue({ tag: rule.tag_id });
    // titleOverride is OFFERED, never applied here. Rewriting the title on a
    // keystroke would replace the very text the user is still typing, and the
    // next character would land on the end of the replacement -- so the rename
    // is a one-click action on the hint instead. The backfill, which runs over
    // finished rows, does apply it automatically.
    setTagRuleHint({
      ruleId: rule.id,
      tagId: rule.tag_id,
      pattern: rule.pattern,
      titleOverride: rule.title_override || null,
    });
  };

  const applyTitleOverride = () => {
    const override = tagRuleHint?.titleOverride;
    if (!override) return;
    // Renaming past the pattern means the rule no longer matches this title, so
    // hold the tag we already set instead of letting the next keystroke
    // re-evaluate and take it back.
    tagTouchedRef.current = true;
    form.setFieldsValue({ title: override });
    setTagRuleHint(null);
  };

  const onClearTagRuleHint = () => {
    // setFieldsValue does NOT fire onValuesChange, so the latch has to be set
    // by hand here -- clearing the suggestion means "no, not that one".
    tagTouchedRef.current = true;
    form.setFieldsValue({ tag: null });
    setTagRuleHint(null);
  };

  const applyMode = (mode, resp) => {
    if (!resp?.split_master) return;
    form.setFieldsValue(splitModeAmounts(mode, resp));
  };
  useEffect(() => {
    if (isMassEdit && props.open) {
      form.resetFields();
      resetTagRuleSession();
      setIntentData(null);
      setActiveDrawerTab('data');
    }
  }, [props.open, isMassEdit]);

  useEffect(() => {
    if (!props.open) return;
    if (isMassEdit) return;
    if (props.editedIntentId) return;
    if (props.defaultValues) {
      form.setFieldsValue(props.defaultValues);
    }
  }, [props.open]);

  useEffect(() => {
    setActiveDrawerTab('data');
    setValidationErrors([]);
    setFormEpoch(e => e + 1);
    setWizardEditMode('all');
    setIncludePastUnpaid(false);
    setIncludePastPaid(false);
    if (props.editedIntentId) {
      try {
        api.get(`/api/intents/${props.editedIntentId}/`, {})
          .then(resp => {
            const respData = intentWithDayjsDates(resp);
            setIntentData(respData);
            form.setFieldsValue(respData);
            if (Array.isArray(resp.split_siblings) && resp.split_siblings.length > 0) {
              form.setFieldsValue({
                project: null,
                project_splits: splitsOfIntent(resp),
              });
              setSplitEditMode('master');
              applyMode('master', resp);
              setActiveEditDimension('split');
            } else if (resp.wizard) {
              setActiveEditDimension('wizard');
            } else {
              setActiveEditDimension(null);
            }
          });
      } catch (error) {
        console.error(error);
      }
    } else {
      form.resetFields();
      resetTagRuleSession();
      setIntentData(null);
      setActiveEditDimension(null);
    }
  }, [props.isOpen, props.editedIntentId, refreshTick]);

  // Keyed on props.open, NOT props.isOpen: every call site passes `open`, so the
  // load effect's isOpen dep above is dead and cannot be relied on to clear
  // per-session state. A leaked latch is silent -- the prefill would simply stop
  // working on the next open -- so this is deliberately belt-and-braces with the
  // resetTagRuleSession() calls beside each form.resetFields().
  useEffect(() => {
    if (!props.open) return;
    resetTagRuleSession();
  }, [props.open, props.editedIntentId, formEpoch]);

  const handleMassEditSave = (setValues) => {
    // massEditScope (useIntentSelection) is either explicit ids or, via the
    // list's select-all, every row its filters match.
    const { qs, body, count } = props.massEditScope;
    const payload = {
      ...body,
      ...setValues,
      ...(setValues.date_invoice_recieved && { date_invoice_recieved: setValues.date_invoice_recieved.format('YYYY-MM-DD') }),
      ...(setValues.date_service_performed && { date_service_performed: setValues.date_service_performed.format('YYYY-MM-DD') }),
      ...(setValues.date_to_be_paid && { date_to_be_paid: setValues.date_to_be_paid.format('YYYY-MM-DD') }),
      ...(setValues.repeat_to_date && { repeat_to_date: setValues.repeat_to_date.format('YYYY-MM-DD') }),
    };
    setSaving(true);
    api.update(`/api/intents/bulk_update/${qs}`, payload, { successMsg: `Posodobljeno ${count} transakcij` })
      .then(() => {
        props.onDone();
        form.resetFields();
        resetTagRuleSession();
        setPendingMassEditValues(null);
        setShowMassEditConfirmModal(false);
      })
      .catch(err => console.error('Mass edit error:', err))
      .finally(() => setSaving(false));
  };

  const handleConfirmedSave = (values, wizardChoice, budgetSyncMode, splitChoice) => {
    const payload = buildSavePayload(values, {
      orgConfig, intentData, isEdit, wizardChoice, budgetSyncMode, splitChoice,
    });
    const { kind, request } = submitIntentSave(payload, {
      id: props.editedIntentId,
      isEdit,
      intentData,
      dimension: activeEditDimension,
      splitEditMode,
      wizardEditMode,
      includePastUnpaid,
      includePastPaid,
    });

    setSaving(true);
    request
      .then(() => {
        props.onDone();
        if (kind === 'plain') {
          if (budgetSyncMode === 'new_version') props.onVersionCreated?.();
          form.resetFields();
          resetTagRuleSession();
        } else {
          setRefreshTick(x => x + 1);
        }
        setPendingValues(null);
        if (kind === 'split') setShowSplitConfirmModal(false);
        else setshowWizardUpdateConfirmModal(false);
      })
      .catch(err => console.error('API error:', err))
      .finally(() => setSaving(false));
    };

  const handleConfirmedDelete = () => {
      // Only the WIZARD dimension scopes deletion across occurrences. Split
      // deletion always cascades to the whole split group of this occurrence,
      // regardless of split mode.
      const isWizard = intentData?.wizard != null;
      const params = new URLSearchParams();
      if (isWizard && activeEditDimension === 'wizard') {
        params.set('wizard_choice', wizardEditMode);
        if (wizardEditMode === 'all') {
          if (includePastUnpaid) params.set('wizard_include_past_unpaid', 'true');
          if (includePastPaid) params.set('wizard_include_past_paid', 'true');
        }
      }
      const qs = params.toString();
      const url = `/api/intents/${props.editedIntentId}/${qs ? `?${qs}` : ''}`;
      api.delete(url, {})
        .then(resp => {
          props.onDone()
          form.resetFields();
          resetTagRuleSession();
          setPendingValues(null);
          setShowDeletionConfirmModal(false);
        })
        .catch(error => {
          console.error(error)
        })
  }

  const handleCancel = () => {
    form.resetFields();
    resetTagRuleSession();
    props.onCancel()
  }

  const onSubmit = () => {
    setValidationErrors([]);
    if (isMassEdit) {
      const allValues = form.getFieldsValue();
      // Include ALL touched fields, even null/undefined — null means "clear this field" (e.g. removing a tag).
      // undefined is converted to null so JSON serialization doesn't silently drop the key.
      const setValues = Object.fromEntries(
        Object.entries(allValues)
          .filter(([key]) => form.isFieldTouched(key))
          // Booleans are NOT NULL in the DB; null here means "brez sprememb", so drop them.
          .filter(([key, v]) => !(NON_NULLABLE_BOOL_FIELDS.includes(key) && v == null))
          .map(([key, v]) => [key, v === undefined ? null : v])
      );
      setPendingMassEditValues(setValues);
      setShowMassEditConfirmModal(true);
      return;
    }
    form
      .validateFields()
      .then((values) => {
        // Active-dimension dispatch. The split confirm modal handles the
        // split dimension; the wizard confirm modal handles the wizard one.
        // Wizard intent (non-split): the mode + past flags are already set via
        // the inline Alert; the confirm modal is info-only.
        const step = confirmStepFor(intentData, activeEditDimension);
        if (step === 'split') {
          setPendingValues(values);
          setShowSplitConfirmModal(true);
        } else if (step === 'wizard') {
          setPendingValues(values);
          setshowWizardUpdateConfirmModal(true);
        } else if (step === 'budget') {
          checkBudgetSync(props.editedIntentId, values)
            .then(resp => {
              if (resp.needs_dialog) {
                setPendingValues(values);
                setShowBudgetSyncModal(true);
              } else {
                handleConfirmedSave(values);
              }
            });
        } else {
          handleConfirmedSave(values);
        }
      })
      .catch((info) => {
        console.error('Validate Failed:', info);
        setValidationErrors(info?.errorFields || []);
      });
  };

  const isIntentEditable = isMassEdit || ['INCOME', 'EXPENSE', 'TRANSFER'].includes(typeValue);
  // One boundary instead of a conditional per field: DefaultFields is left
  // untouched so company mode stays byte-identical.
  const FieldsComponent = orgConfig.single_date ? PersonalFields : DefaultFields;
  const isCreation = props.editedIntentId == null && !isMassEdit;

  // Changing the type after the fact, which used to be barred outright.
  //
  // An expense and an income are the same row with the direction read off the
  // type, so the two can simply be swapped. A transfer is a PAIR of rows whose
  // second leg would have to be created or deleted, and a DDV row is generated
  // and rebuilt on the next tax run -- neither converts by editing one field,
  // so both stay locked. The API enforces the same rule; this only keeps the
  // control honest about it.
  // DDV rows are generated out of the transactions around them and rebuilt on
  // the next tax run, so their type is the one that stays fixed. Everything
  // else converts, including in and out of a transfer -- which costs a second
  // row, created or deleted; see the notices below.
  const typeSwappable = ['EXPENSE', 'INCOME', 'TRANSFER'].includes(intentData?.type);
  const editingOneSplitRow = intentData?.split_siblings && splitEditMode === 'child';
  const typeLocked = isEdit && (!typeSwappable || editingOneSplitRow);

  // Two different questions, and they had been answered by one variable: the
  // split message ended up on the greyed options of every ordinary
  // transaction, none of which is a split.
  //
  // Why the whole control is frozen -- only ever read when it IS frozen:
  const typeLockedReason = !typeSwappable
    ? 'Vrstice DDV nastanejo samodejno in jih ni mogoče pretvoriti.'
    : 'Vrsto razdeljene transakcije spremenite na glavni vrstici.';
  // Why DDV can never be picked, which is true of every existing transaction:
  const typeConvertReason = 'Vrstice DDV nastanejo samodejno iz drugih transakcij.';

  // Converting in or out of a transfer adds or removes a row, and one of those
  // is destructive. Neither should be a surprise discovered after saving.
  const becomingTransfer = isEdit
    && intentData?.type && intentData.type !== 'TRANSFER' && typeValue === 'TRANSFER';
  const leavingTransfer = isEdit
    && intentData?.type === 'TRANSFER' && typeValue && typeValue !== 'TRANSFER';
  // In a bulk edit the rows' current types are not in hand, so the notice has
  // to cover both directions at once.
  const massToTransfer = isMassEdit && typeValue === 'TRANSFER';
  const massFromTransfer = isMassEdit && ['EXPENSE', 'INCOME'].includes(typeValue);
  const isReadOnly = isMassEdit ? false : (intentData?.id && !isIntentEditable);
  const taxRateReadOnly =
    ['TAX', 'TRANSFER'].includes(typeValue)
    || (intentData?.split_siblings && splitEditMode === 'child');

  useEffect(() => {
    if (!orgConfig.tax_enabled || typeValue === 'TRANSFER') {
      form.setFieldsValue({ tax_rate: 0 });
    }
  }, [typeValue]);

  // Swapping expense <-> income swaps which date is required: an expense wants
  // "Prejeto", an income wants "Opravljeno", and the server refuses to save
  // without the right one. Carrying the date across means changing the type is
  // one click rather than one click and a date the user has to look up again.
  //
  // Only on an actual change, never on first render, or simply opening an
  // expense would quietly fill in a date it never had.
  const prevTypeRef = useRef(undefined);
  useEffect(() => {
    const prev = prevTypeRef.current;
    prevTypeRef.current = typeValue;

    if (prev === undefined || prev === typeValue) return;
    if (orgConfig.single_date) return;  // personal mode keeps one date only
    if (!['EXPENSE', 'INCOME'].includes(typeValue)) return;

    const values = form.getFieldsValue();
    const carried = values.date_invoice_recieved
      || values.date_service_performed
      || values.date_to_be_paid;
    if (!carried) return;

    if (typeValue === 'INCOME' && !values.date_service_performed) {
      form.setFieldsValue({ date_service_performed: carried });
    }
    if (typeValue === 'EXPENSE' && !values.date_invoice_recieved) {
      form.setFieldsValue({ date_invoice_recieved: carried });
    }
  }, [typeValue]);

  // The four amount fields recalculate away from whichever one was just edited:
  // that field is never written back, the other three are derived from it. Kept
  // synchronous and client-side (the old /calc_amounts/ round-trip landed ~1s
  // later, rewriting fields under the caret and re-triggering itself).
  //
  // Every branch leaves amount_no_tax + tax_amount === amount exactly, so the
  // server can store the triple verbatim instead of re-deriving it from the 2dp
  // tax_rate — see apply_amounts() in backend/intents/procedures.py.
  const recalcFrom = (field) => {
    if (!orgConfig.tax_enabled) return;  // single amount field, nothing to derive

    const values = form.getFieldsValue();
    const net = num(values.amount_no_tax);
    const gross = num(values.amount);
    const vat = num(values.tax_amount);
    const rate = num(values.tax_rate);

    // TAX / TRANSFER / split children have a fixed rate and no DDV field, so the
    // rate stays the anchor there and is never re-derived from the two amounts.
    if (taxRateReadOnly) {
      if (rate == null) return;
      if (field === 'amount') {
        if (gross == null) return;
        const n = round2(gross / (1 + rate / 100));
        form.setFieldsValue({ amount_no_tax: n, tax_amount: round2(gross - n) });
      } else if (net != null) {
        const g = round2(net * (1 + rate / 100));
        form.setFieldsValue({ amount: g, tax_amount: round2(g - net) });
      }
      return;
    }

    if (field === 'amount_no_tax' || field === 'tax_rate') {
      // Net + rate anchor: derive gross, then DDV as the remainder.
      if (net == null || rate == null) return;
      const g = round2(net * (1 + rate / 100));
      form.setFieldsValue({ amount: g, tax_amount: round2(g - net) });
      return;
    }

    if (field === 'tax_amount') {
      // The typed DDV wins: gross is the sum, the rate is the derived label.
      if (vat == null) return;
      const anchor = net != null ? net : (gross != null ? round2(gross - vat) : null);
      if (anchor == null || anchor === 0) return;
      form.setFieldsValue({
        amount_no_tax: anchor,
        amount: round2(anchor + vat),
        tax_rate: round2((vat / anchor) * 100),
      });
      return;
    }

    if (field === 'amount') {
      // The typed gross wins. Anchor on net when there is one; with no net yet
      // (gross entered first) fall back to splitting it by the current rate.
      if (gross == null) return;
      if (net != null && net !== 0) {
        const t = round2(gross - net);
        form.setFieldsValue({ tax_amount: t, tax_rate: round2((t / net) * 100) });
      } else if (rate != null) {
        const n = round2(gross / (1 + rate / 100));
        form.setFieldsValue({ amount_no_tax: n, tax_amount: round2(gross - n) });
      }
    }
  };

  const taxIntentInfoComponent = intentData?.tax_intent_title ? <span>{intentData?.tax_intent_title}</span> : null;

  const taxAmountOverridden = typeValue === 'TAX' && intentData?.manually_overridden_fields?.includes('amount');

  // Live divergence: the TAX amount currently in the form differs from the
  // system-computed value — even before saving. Lets us surface the info label
  // + "Ponastavi" the moment the user edits the amount, not only after save.
  const taxAmountDiverges =
    typeValue === 'TAX' &&
    intentData?.tax_system_amount != null &&
    amount != null &&
    Number(amount) !== Number(intentData.tax_system_amount);

  const showTaxOverrideInfo = taxAmountOverridden || taxAmountDiverges;

  const handleResetAmountOverride = () => {
    api
      .post(`/api/intents/${props.editedIntentId}/reset_amount_override/`, {}, { successMsg: 'Znesek ponastavljen na sistemsko vrednost' })
      .then(() => {
        setRefreshTick(x => x + 1);
        props.onIntentChanged?.();
      });
  };

  // Open the intents list filtered to this TAX intent's related income/expense
  // transactions (those summed into this taxation period).
  const handleShowRelatedIntents = () => {
    const id = props.editedIntentId;
    if (!id) return;
    const filter = { tax_intent: id, page: 1 };
    dispatch(setFilterData(filter));
    navigate(`/intents#${new URLSearchParams(filter).toString()}`);
    props.onCancel?.();
  };

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        onValuesChange={(changed) => {
          // Manually picking a tax rate anchors on net (amount_no_tax) and
          // recomputes gross + DDV. onValuesChange only fires for user input,
          // not for our own setFieldsValue (e.g. the DDV→rate derivation), so
          // it can't fight the other recalculation paths.
          if ('tax_rate' in changed) {
            recalcFrom('tax_rate');
          }

          // That same "user input only" property is what makes the tag prefill
          // work: our own setFieldsValue below cannot re-enter here and latch
          // the feature off against itself.
          if ('tag' in changed) {
            // The user picked or cleared the tag -- either way they now own the
            // field for the rest of this drawer session. Handled before 'title'
            // so one change batch can never prefill over a deliberate choice.
            tagTouchedRef.current = true;
            setTagRuleHint(null);
          }
          if ('title' in changed) {
            maybePrefillTag(changed.title);
          }
        }}
      >
        <Drawer
          title={
            <Space>
              <span>
                {isMassEdit
                  ? `Množično urejanje (${props.massEditScope?.count} transakcij)`
                  : isCreation
                  ? "Ustvari novo transakcijo"
                  : `Transakcija #${props.editedIntentId}`}
              </span>
              {!isCreation && !isMassEdit && (
                <Segmented
                  size="small"
                  value={activeDrawerTab}
                  onChange={setActiveDrawerTab}
                  options={[
                    { label: 'Podatki', value: 'data' },
                    {
                      label: `Komentarji${intentData?.comments_count ? ` (${intentData.comments_count})` : ''}`,
                      value: 'comments',
                    },
                    { label: 'Zgodovina', value: 'history' },
                  ]}
                />
              )}
            </Space>
          }
          width={750}
          onClose={handleCancel}
          open={props.open}
          extra={
            activeDrawerTab === 'data' && (
              <Space>
                {!isMassEdit && <Button disabled={!isIntentEditable || isCreation} onClick={() => setShowDeletionConfirmModal(true)} danger><DeleteOutlined /></Button>}
                <Button onClick={handleCancel}><CloseOutlined /></Button>
                <Button disabled={saving} loading={saving} onClick={onSubmit} type="primary">Shrani</Button>
              </Space>
            )
          }
        >
          {activeDrawerTab === 'data' && (
                <>
                  {validationErrors.length > 0 && (
                    <Alert
                      type="error"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Obrazec ima napake"
                      description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {validationErrors.map((f, i) => (
                            <li key={i}>
                              {f.errors?.[0] ||
                                (Array.isArray(f.name) ? f.name.join('.') : f.name)}
                            </li>
                          ))}
                        </ul>
                      }
                    />
                  )}
                  {isEdit && ['INCOME', 'EXPENSE'].includes(typeValue) && intentData?.tax_period_locked && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Davčna obveznost za to obdobje je ročno nastavljena (zaklenjena)."
                      description={
                        <>
                          Sprememba te transakcije bo posodobila sistemsko izračunan znesek, ne pa ročno nastavljenega (zaklenjenega) davčnega zneska. Po potrebi ponastavi davčni znesek na davčni obveznosti.
                          {props.onOpenIntent && intentData?.tax_intent && (
                            <div style={{ marginTop: 4 }}>
                              <Button
                                type="link"
                                size="small"
                                style={{ paddingLeft: 0 }}
                                onClick={() => props.onOpenIntent(intentData.tax_intent)}
                              >
                                Odpri davčno obveznost{intentData?.tax_intent_title ? ` (${intentData.tax_intent_title})` : ''} →
                              </Button>
                            </div>
                          )}
                        </>
                      }
                    />
                  )}
                  {intentData?.split_siblings && (() => {
                    const isActive = activeEditDimension === 'split';
                    return (
                      <Alert
                        type="info"
                        showIcon
                        style={{
                          marginBottom: 12,
                          opacity: isActive ? 1 : 0.55,
                          borderLeft: isActive ? '4px solid #1677ff' : '4px solid transparent',
                          cursor: isActive ? 'default' : 'pointer',
                        }}
                        onClick={isActive ? undefined : () => setActiveEditDimension('split')}
                        message={
                          `Ta transakcija je razdeljena med ${intentData.split_siblings.length} ` +
                          (intentData.split_siblings.length === 2 ? 'projekta' : 'projekte')
                        }
                        description={
                          <>
                            <Space>
                              <span
                                style={{
                                  fontWeight: splitEditMode === 'master' ? 600 : 400,
                                  opacity: splitEditMode === 'master' ? 1 : 0.55,
                                }}
                              >
                                {`Urejanje VSEH projektnih transakcij (${intentData.split_siblings.length})`}
                              </span>
                              <Switch
                                checked={splitEditMode === 'child'}
                                disabled={!isActive}
                                onChange={(checked) => {
                                  const v = checked ? 'child' : 'master';
                                  setSplitEditMode(v);
                                  applyMode(v, intentData);
                                }}
                              />
                              <span
                                style={{
                                  fontWeight: splitEditMode === 'child' ? 600 : 400,
                                  opacity: splitEditMode === 'child' ? 1 : 0.55,
                                }}
                              >
                                Urejanje TRENUTNE PROJEKTNE transakcije (omejeno)
                              </span>
                            </Space>
                            {!isActive && (
                              <div style={{ fontStyle: 'italic', marginTop: 4 }}>
                                Klikni za urejanje razdelitve
                              </div>
                            )}
                          </>
                        }
                      />
                    );
                  })()}
                  {intentData?.wizard && (() => {
                    const isActive = activeEditDimension === 'wizard';
                    const activateWizard = () => {
                      setActiveEditDimension('wizard');
                      // On a split intent, wizard-dimension edits the master
                      // amount across occurrences — show the master total.
                      if (intentData?.split_siblings) {
                        setSplitEditMode('master');
                        applyMode('master', intentData);
                      }
                    };
                    return (
                      <Alert
                        type="info"
                        showIcon
                        style={{
                          marginBottom: 12,
                          opacity: isActive ? 1 : 0.55,
                          borderLeft: isActive ? '4px solid #1677ff' : '4px solid transparent',
                          cursor: isActive ? 'default' : 'pointer',
                        }}
                        onClick={isActive ? undefined : activateWizard}
                        message="Ta transakcija se ponavlja"
                        description={
                          <>
                            <Space>
                              <span
                                style={{
                                  fontWeight: wizardEditMode === 'all' ? 600 : 400,
                                  opacity: wizardEditMode === 'all' ? 1 : 0.55,
                                }}
                              >
                                {`Urejanje VSEH ponovljajočih transakcij${
                                  intentData?.wizard_occurrence_count
                                    ? ` (${intentData.wizard_occurrence_count})`
                                    : ''
                                }`}
                              </span>
                              <Switch
                                checked={wizardEditMode === 'single'}
                                disabled={!isActive}
                                onChange={(checked) =>
                                  setWizardEditMode(checked ? 'single' : 'all')
                                }
                              />
                              <span
                                style={{
                                  fontWeight: wizardEditMode === 'single' ? 600 : 400,
                                  opacity: wizardEditMode === 'single' ? 1 : 0.55,
                                }}
                              >
                                Urejanje samo TRENUTNE transakcije
                              </span>
                            </Space>
                            {wizardEditMode === 'all' && (
                              <div style={{ marginTop: 8 }}>
                                <Checkbox
                                  checked={includePastUnpaid}
                                  disabled={!isActive}
                                  onChange={(e) => setIncludePastUnpaid(e.target.checked)}
                                >
                                  Vključi pretekle neplačane ponovitve
                                </Checkbox>
                                <br />
                                <Checkbox
                                  checked={includePastPaid}
                                  disabled={!isActive}
                                  onChange={(e) => setIncludePastPaid(e.target.checked)}
                                >
                                  Vključi že plačane ponovitve
                                </Checkbox>
                              </div>
                            )}
                            {!isActive && (
                              <div style={{ fontStyle: 'italic', marginTop: 4 }}>
                                Klikni za urejanje ponovitev
                              </div>
                            )}
                          </>
                        }
                      />
                    );
                  })()}
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="type"
                        label="Tip"
                        rules={isMassEdit ? [] : [{ required: true, message: 'Prosim izberite tip' }]}
                      >
                        <IntentTypeSelect
                          disabled={typeLocked}
                          lockedReason={typeLocked ? typeLockedReason : undefined}
                          disabledOpts={(isEdit || isMassEdit) ? ['TAX'] : []}
                          disabledOptsReason={typeConvertReason}
                          hidden={[...(isCreation ? ['TAX'] : []), ...(orgConfig.tax_enabled ? [] : ['TAX'])]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {leavingTransfer && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Druga stran prenosa bo izbrisana"
                      description="Prenos sta dve vrstici, po ena na vsakem računu. Ko to ni več prenos, nasprotna vrstica nima več česa izravnati in se ob shranjevanju odstrani."
                    />
                  )}
                  {becomingTransfer && (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Ustvarjena bo še nasprotna vrstica"
                      description="Prenos sta dve vrstici. Ob shranjevanju nastane nasprotna vrstica na ciljnem računu, znesek pa se razdeli med računa."
                    />
                  )}
                  {massToTransfer && (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Za vsako izbrano transakcijo bo ustvarjena nasprotna vrstica"
                      description="Prenos sta dve vrstici. Izberite izvorni in ciljni račun — brez njiju sprememba ne bo shranjena."
                    />
                  )}
                  {massFromTransfer && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Prenosi med izbranimi bodo izgubili nasprotno vrstico"
                      description="Če je med izbranimi kakšen prenos, se ob shranjevanju odstrani vrstica, ki ga je izravnavala. Če sta izbrani obe strani istega prenosa, ostane ena vrstica."
                    />
                  )}

                  {(typeValue != null || isMassEdit) && !orgConfig.tax_enabled && (
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          name="amount"
                          label={<OverrideLabel label="Znesek" field="amount" overriddenFields={intentData?.manually_overridden_fields} />}
                          rules={isMassEdit ? [] : [{ required: true, message: 'Prosim vnesite znesek' }]}
                        >
                          <DebouncedNumberInput
                            value={form.getFieldValue('amount')}
                            onChange={(val) => form.setFieldsValue({ amount: val })}
                            disabled={isReadOnly}
                            placeholder="Vnesite znesek"
                            min={0}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  {(typeValue != null || isMassEdit) && orgConfig.tax_enabled && (
                    <>
                    <Row justify="space-between">
                      <Col span={11}>
                        <Form.Item
                          name="amount_no_tax"
                          label={<OverrideLabel label="Znesek brez DDV" field="amount_no_tax" overriddenFields={intentData?.manually_overridden_fields} />}
                          rules={isMassEdit ? [] : [{ required: true, message: 'Prosim vnesite znesek' }]}
                          >
                          <DebouncedNumberInput
                            value={form.getFieldValue('amount_no_tax')}
                            onChange={(val) => {
                              form.setFieldsValue({ amount_no_tax: val });
                              recalcFrom('amount_no_tax');
                            }}
                            disabled={isReadOnly || (intentData?.split_siblings && splitEditMode === 'child')}
                            placeholder="Vnesite znesek"
                            min={typeValue === 'TAX' ? -Infinity : 0}
                            />
                        </Form.Item>
                      </Col>

                      <Col span={11}>
                        <Form.Item
                          name="amount"
                          label={<OverrideLabel label="Znesek z DDV" field="amount" overriddenFields={intentData?.manually_overridden_fields} />}
                          rules={isMassEdit ? [] : [{ required: true, message: 'Prosim vnesite znesek' }]}
                          extra={typeValue === 'TAX' ? 'Uredi za spreminjanje vrednosti DDV.' : undefined}
                          >
                          <DebouncedNumberInput
                            value={form.getFieldValue('amount')}
                            onChange={(val) => {
                              form.setFieldsValue({ amount: val });
                              recalcFrom('amount');
                            }}
                            disabled={(isReadOnly && typeValue !== 'TAX') || (intentData?.split_siblings && splitEditMode === 'child')}
                            placeholder="Vnesite znesek"
                            min={typeValue === 'TAX' ? -Infinity : 0}
                            />
                        </Form.Item>
                        {showTaxOverrideInfo && (
                          <div style={{ marginTop: -8, marginBottom: 8, fontSize: 12, color: '#888' }}>
                            <EditOutlined style={{ color: '#1677ff', marginRight: 4 }} />
                            {taxAmountOverridden ? (
                              <>Ročno spremenil: <b>{intentData?.amount_overridden_by || 'neznano'}</b></>
                            ) : (
                              <>Ročno spreminjate davčni znesek.</>
                            )}
                            {intentData?.tax_system_amount != null && (
                              <><br />Sistemsko izračunan znesek: <MoneyDisplay type={null} val={intentData?.tax_system_amount} /></>
                            )}
                            <Button type="link" size="small" style={{ paddingLeft: 6 }} onClick={handleResetAmountOverride}>
                              Ponastavi
                            </Button>
                          </div>
                        )}
                      </Col>
                    </Row>

                    <Row justify="space-between">
                      <Col span={!taxRateReadOnly && !isMassEdit ? 13 : 24}>
                        <Form.Item
                          name="tax_rate"
                          label={<OverrideLabel label="Davčna stopnja" field="tax_rate" overriddenFields={intentData?.manually_overridden_fields} />}
                          rules={taxRateReadOnly ? [] : [{ required: true, message: 'Prosim vnesite davčno stopnjo' }]}
                        >
                          <TaxRateSelect key={formEpoch} disabled={taxRateReadOnly} />
                        </Form.Item>
                      </Col>

                      {!taxRateReadOnly && !isMassEdit && (
                        <Col span={10}>
                          <Form.Item
                            name="tax_amount"
                            label={<OverrideLabel label="Znesek DDV" field="tax_amount" overriddenFields={intentData?.manually_overridden_fields} />}
                            extra="Vnesi želeni znesek DDV — davčna stopnja se izračuna samodejno."
                          >
                            <DebouncedNumberInput
                              value={form.getFieldValue('tax_amount')}
                              onChange={(val) => {
                                form.setFieldsValue({ tax_amount: val });
                                recalcFrom('tax_amount');
                              }}
                              placeholder="Vnesite znesek DDV"
                              />
                          </Form.Item>
                        </Col>
                      )}
                    </Row>
                    </>
                  )}

                  {intentData?.tax_amount && intentData.type != 'TAX' && (
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          name="tax_intent_title"
                          label="Knjižba"
                        >
                          {taxIntentInfoComponent || <span style={{ color: '#aaa', fontSize: 12 }}>Vrednost bo prikazana po kreaciji transakcije</span>}
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  <Divider/>

                  {typeValue == 'TRANSFER' && <TransferFields />}

                  {typeValue == 'TAX' && (
                    <TaxFields
                      onShowRelated={isEdit ? handleShowRelatedIntents : null}
                    />
                  )}

                  {(isMassEdit ? (!typeValue || ['INCOME', 'EXPENSE'].includes(typeValue)) : ['INCOME', 'EXPENSE'].includes(typeValue)) && (
                    <FieldsComponent
                      typeValue={typeValue}
                      isIntentEditable={isIntentEditable}
                      repeatPeriod={repeatPeriod}
                      taxIntentInfoComponent={taxIntentInfoComponent}
                      massEdit={isMassEdit}
                      overriddenFields={intentData?.manually_overridden_fields}
                      splitModalOpen={splitModalOpen}
                      setSplitModalOpen={setSplitModalOpen}
                      isEdit={isEdit}
                      editedIntentId={props.editedIntentId}
                      onSplitsUpdated={() => {
                        setRefreshTick(x => x + 1);
                        props.onDone?.();
                      }}
                      splitEditMode={
                        intentData?.split_siblings ? splitEditMode : null
                      }
                      activeEditDimension={activeEditDimension}
                      tagRuleHint={tagRuleHint}
                      onClearTagRuleHint={onClearTagRuleHint}
                      onApplyTitleOverride={applyTitleOverride}
                      onCreateTagRule={() =>
                        setTagRuleDrawerDefaults({
                          // The raw title is rarely the rule you want
                          // ("PETROL Ljubljana 12.3.2026"), so the drawer leaves
                          // it editable.
                          pattern: form.getFieldValue('title') || '',
                          tag: form.getFieldValue('tag'),
                        })
                      }
                    />
                  )}
                </>
          )}
          {activeDrawerTab === 'comments' && (
            <IntentComments
              intentId={props.editedIntentId}
              isFlagged={intentData?.is_flagged}
              onFlagChange={(val) => {
                setIntentData((prev) => prev ? { ...prev, is_flagged: val } : prev);
                props.onIntentChanged?.();
              }}
              onCountChange={(count) => {
                setIntentData((prev) => prev ? { ...prev, comments_count: count } : prev);
              }}
            />
          )}
          {activeDrawerTab === 'history' && (
            <HistoryTimeline model="Intent" objectId={props.editedIntentId} />
          )}
        </Drawer>
      </Form>

        {/* Sibling of the Form, never nested inside it: antd Drawers portal to
            body so no nested <form> element actually occurs, but a nested Form
            context would let Enter-to-submit reach the wrong handler. */}
        <TagRuleDrawer
          open={tagRuleDrawerDefaults !== null}
          id={null}
          defaultValues={tagRuleDrawerDefaults}
          onCancel={() => setTagRuleDrawerDefaults(null)}
          onDone={() => {
            setTagRuleDrawerDefaults(null);
            dispatch(loadTagRules());
          }}
        />

        <BudgetSyncModal
          open={showBudgetSyncModal}
          onOk={(syncMode) => {
            setShowBudgetSyncModal(false);
            handleConfirmedSave(pendingValues, null, syncMode);
          }}
          onCancel={() => {
            setShowBudgetSyncModal(false);
            setPendingValues(null);
          }}
        />

        <WizardUpdateConfirmModal
          open={showWizardUpdateConfirmModal}
          mode={wizardEditMode}
          onOk={() => {
            setshowWizardUpdateConfirmModal(false);
            handleConfirmedSave(pendingValues, wizardEditMode);
          }}
          onCancel={() => {
            setshowWizardUpdateConfirmModal(false);
            setPendingValues(null);
          }}
        />

        <SplitUpdateConfirmModal
          open={showSplitConfirmModal}
          mode={splitEditMode}
          onOk={() => {
            setShowSplitConfirmModal(false);
            handleConfirmedSave(
              pendingValues,
              null,
              null,
              splitEditMode === 'child' ? 'single' : 'all',
            );
          }}
          onCancel={() => {
            setShowSplitConfirmModal(false);
            setPendingValues(null);
          }}
        />

        <ConfirmModal
          textComponent={
            <>
              <p>{`Ali ste prepričani, da želite izbrisati transakcijo #${intentData?.id}?`}</p>
              {intentData?.split_siblings && (
                <p style={{ color: '#d4380d' }}>
                  Pozor: izbrisana bo cela razdelitev (master + vse vrstice te ponovitve).
                </p>
              )}
              {intentData?.wizard && activeEditDimension === 'wizard' && (
                <div style={{ marginTop: 12 }}>
                  <Space>
                    <span
                      style={{
                        fontWeight: wizardEditMode === 'all' ? 600 : 400,
                        opacity: wizardEditMode === 'all' ? 1 : 0.55,
                      }}
                    >
                      Izbris VSEH ponovitev
                    </span>
                    <Switch
                      checked={wizardEditMode === 'single'}
                      onChange={(checked) =>
                        setWizardEditMode(checked ? 'single' : 'all')
                      }
                    />
                    <span
                      style={{
                        fontWeight: wizardEditMode === 'single' ? 600 : 400,
                        opacity: wizardEditMode === 'single' ? 1 : 0.55,
                      }}
                    >
                      Izbris samo TE ponovitve
                    </span>
                  </Space>
                  {wizardEditMode === 'all' && (
                    <div style={{ marginTop: 8 }}>
                      <Checkbox
                        checked={includePastUnpaid}
                        onChange={(e) => setIncludePastUnpaid(e.target.checked)}
                      >
                        Vključi pretekle neplačane ponovitve
                      </Checkbox>
                      <br />
                      <Checkbox
                        checked={includePastPaid}
                        onChange={(e) => setIncludePastPaid(e.target.checked)}
                      >
                        Vključi že plačane ponovitve
                      </Checkbox>
                    </div>
                  )}
                </div>
              )}
              {intentData?.wizard && activeEditDimension !== 'wizard' && (
                <p style={{ fontStyle: 'italic', color: '#8c8c8c' }}>
                  Druge ponovitve ostanejo nespremenjene (urejaš razdelitev,
                  ne ponovitev). Za izbris več ponovitev preklopi na "ponovitev"
                  v zgornjem info-okviru.
                </p>
              )}
            </>
          }
          open={showDeletionConfirmModal}
          confirmBtnTitle='Izbriši'
          confirmBtnProps={{'danger': true}}
          onOk={() => handleConfirmedDelete()}
          onCancel={() => {
            setShowDeletionConfirmModal(false);
          }}
        />

        <ConfirmModal
          textComponent={<p>{`Ali ste prepričani, da želite posodobiti ${props.massEditScope?.count} transakcij?`}</p>}
          open={showMassEditConfirmModal}
          confirmBtnTitle='Posodobi'
          onOk={() => handleMassEditSave(pendingMassEditValues)}
          onCancel={() => {
            setShowMassEditConfirmModal(false);
            setPendingMassEditValues(null);
          }}
        />
    </>
  );
};

export default IntentDrawer;
